import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { useStore } from "@/store/StoreContext";
import { tx } from "@/components/ui";
import { CheckoutIssuesBanner } from "@/components/CheckoutIssuesBanner";

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `block rounded-lg px-3 py-2 text-sm ${isActive ? "bg-brand text-white" : "text-gray-700 hover:bg-gray-100"}`;
const subCls = ({ isActive }: { isActive: boolean }) =>
  `block rounded-lg px-3 py-1.5 text-sm ${isActive ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-100"}`;
const SETTINGS_PATHS = ["/app/services", "/app/staff", "/app/shifts", "/app/leave", "/app/resources", "/app/settings"];
const KEY = "studio.nav.settingsOpen";

function Caption({ children }: { children: string }) {
  return <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{children}</div>;
}

/** Sidebar in three groups: daily work, operations, and a collapsible Settings section (open on settings pages). */
export function AdminShell() {
  const { me, logout } = useAuth();
  const { stores, store, setStoreId } = useStore();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const onSettings = SETTINGS_PATHS.some((p) => pathname.startsWith(p));
  const [open, setOpen] = useState<boolean>(() => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } });
  useEffect(() => { if (onSettings) setOpen(true); }, [onSettings]);
  const toggle = () => { const v = !open; setOpen(v); try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* private mode */ } };
  const canSettings = me?.roles.some((r) => r.role === "owner" || r.role === "admin");
  const canMoney = me?.roles.some((r) => ["owner", "admin", "manager", "accountant"].includes(r.role));
  const ver = useQuery({ queryKey: ["version"], queryFn: () => api<{ version: string; build: string }>("/public/version"), staleTime: Infinity, retry: false });
  const unread = useQuery({ queryKey: ["unread-count", store?.brand_id], queryFn: () => api<{ count: number }>(`/app/threads/unread-count?brand_id=${store!.brand_id}`), enabled: !!store, refetchInterval: 30000, retry: false });
  return (
    <div className="min-h-screen md:grid md:grid-cols-[220px_1fr]">
      <aside className="border-b md:border-b-0 md:border-r bg-white p-4 space-y-2">
        <div className="font-semibold text-accent">{t("app.title")}</div>
        <label className="block text-xs text-gray-500">{t("nav.store")}
          <select className="mt-1 w-full rounded-lg border px-2 py-1 text-sm" value={store?.id ?? ""} onChange={(e) => setStoreId(e.target.value)}>
            {[...new Map(stores.map((s) => [s.brand_id, s.brand])).values()].map((b) => (
              <optgroup key={b.id} label={tx(b.name, i18n.language)}>
                {stores.filter((s) => s.brand_id === b.id).map((s) => <option key={s.id} value={s.id}>{tx(s.name, i18n.language)}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <nav className="space-y-1 pt-1">
          <Caption>{t("nav.groupDaily")}</Caption>
          <NavLink to="/app" end className={linkCls}>{t("nav.dashboard")}</NavLink>
          <NavLink to="/app/calendar" className={linkCls}>{t("nav.calendar")}</NavLink>
          <NavLink to="/app/appointments" className={linkCls}>{t("nav.appointments")}</NavLink>
          <NavLink to="/app/customers" className={linkCls}>{t("nav.customers")}</NavLink>
          <NavLink to="/app/messages" className={linkCls}>{t("nav.messages")}{unread.data && unread.data.count > 0 && <span className="ml-2 rounded-full bg-red-600 px-1.5 text-[11px] text-white">{unread.data.count}</span>}</NavLink>

          <div className="my-2 border-t" />
          <Caption>{t("nav.groupOps")}</Caption>
          <NavLink to="/app/attendance" className={linkCls}>{t("nav.attendance")}</NavLink>
          {canMoney && <NavLink to="/app/expenses" className={linkCls}>{t("nav.expenses")}</NavLink>}
          {canSettings && store && <NavLink to={`/app/settings/packages/${store.brand_id}`} className={linkCls}>{t("nav.packages")}</NavLink>}

          <div className="my-2 border-t" />
          <button type="button" className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm ${onSettings ? "font-medium text-gray-900" : "text-gray-700"} hover:bg-gray-100`} onClick={toggle} aria-expanded={open}>
            <span>⚙︎ {t("nav.settings")}</span><span className="text-xs text-gray-400">{open ? "▾" : "▸"}</span>
          </button>
          {open && (
            <div className="ml-2 space-y-0.5 border-l pl-2">
              {canSettings && store && <NavLink to={`/app/settings/brand/${store.brand_id}`} className={subCls}>{t("nav.brand")}</NavLink>}
              {canSettings && store && <NavLink to={`/app/settings/store/${store.id}`} className={subCls}>{t("nav.storeSettings")}</NavLink>}
              <NavLink to="/app/services" className={subCls}>{t("nav.services")}</NavLink>
              <NavLink to="/app/staff" className={subCls}>{t("nav.staff")}</NavLink>
              <NavLink to="/app/shifts" className={subCls}>{t("nav.shifts")}</NavLink>
              <NavLink to="/app/leave" className={subCls}>{t("nav.leave")}</NavLink>
              <NavLink to="/app/resources" className={subCls}>{t("nav.resources")}</NavLink>
              {canSettings && <NavLink to="/app/settings/users" className={subCls}>{t("nav.users")}</NavLink>}
              {canSettings && <NavLink to="/app/settings/custom-fields" className={subCls}>{t("nav.customFields")}</NavLink>}
              {canSettings && store && <NavLink to={`/app/settings/messaging/${store.brand_id}`} className={subCls}>{t("nav.messaging")}</NavLink>}
              {canSettings && store && <NavLink to={`/app/settings/notifications/${store.brand_id}`} className={subCls}>{t("nav.notifications")}</NavLink>}
            </div>
          )}
        </nav>
        <div className="pt-4 text-xs text-gray-500">{me?.email}</div>
        <div className="flex gap-3 text-xs">
          <button className="underline" onClick={() => i18n.changeLanguage(i18n.language === "el" ? "en" : "el")}>{i18n.language === "el" ? "EN" : "EL"}</button>
          <button className="underline" onClick={() => logout()}>{t("auth.logout")}</button>
        </div>
        {ver.data && <div className="pt-2 text-[11px] text-gray-400" title={ver.data.build}>v{ver.data.version} · {ver.data.build.split(" ")[0]}</div>}
      </aside>
      <main className="p-4 md:p-6"><CheckoutIssuesBanner /><Outlet /></main>
    </div>
  );
}
